export type PropertyFieldKey =
  | "uso"
  | "alias"
  | "rental_price"
  | "maintenance_price"
  | "street"
  | "exterior_number"
  | "interior_number"
  | "zip_code"
  | "colonia"
  | "city"
  | "state";

export const PROPERTY_FIELD_KEYS: PropertyFieldKey[] = [
  "uso",
  "alias",
  "rental_price",
  "maintenance_price",
  "street",
  "exterior_number",
  "interior_number",
  "zip_code",
  "colonia",
  "city",
  "state",
];

export interface PropertyFieldValues {
  uso?: string;
  alias?: string;
  rental_price?: string;
  maintenance_price?: string;
  street?: string;
  exterior_number?: string;
  interior_number?: string;
  zip_code?: string;
  colonia?: string;
  city?: string;
  state?: string;
}

export interface PropertyValidationOptions {
  requireUso?: boolean;
}

const numericFromCurrency = (value?: string) => {
  if (!value) return NaN;
  const digits = value.replace(/[^\d.-]/g, "");
  return Number(digits);
};

export const getPropertyFieldError = (
  field: PropertyFieldKey,
  values: PropertyFieldValues,
  options: PropertyValidationOptions = {},
): string | undefined => {
  const requireUso = options.requireUso ?? true;

  switch (field) {
    case "uso": {
      const uso = values.uso?.trim() ?? "";
      if (requireUso && uso === "") {
        return "El uso del inmueble es requerido";
      }
      if (uso.length > 50) {
        return "El uso no puede tener más de 50 caracteres";
      }
      return undefined;
    }
    case "alias": {
      const alias = values.alias?.trim() ?? "";
      if (alias !== "" && alias.length > 50) {
        return "El alias no puede tener más de 50 caracteres";
      }
      return undefined;
    }
    case "rental_price": {
      const rental = numericFromCurrency(values.rental_price);
      if (!values.rental_price || values.rental_price.trim() === "") {
        return "El valor de la renta es requerido";
      }
      if (!Number.isFinite(rental) || rental <= 0) {
        return "El valor de la renta debe ser un número positivo";
      }
      return undefined;
    }
    case "maintenance_price": {
      if (!values.maintenance_price || values.maintenance_price.trim() === "") {
        return undefined;
      }
      const maintenance = numericFromCurrency(values.maintenance_price);
      if (!Number.isFinite(maintenance) || maintenance < 0) {
        return "El valor del mantenimiento debe ser un número positivo o cero";
      }
      return undefined;
    }
    case "street": {
      const street = values.street?.trim() ?? "";
      if (street === "") {
        return "La calle es requerida";
      }
      if (street.length > 80) {
        return "La calle no puede tener más de 80 caracteres";
      }
      return undefined;
    }
    case "exterior_number": {
      const exterior = values.exterior_number?.trim() ?? "";
      if (exterior === "") {
        return "El número exterior es requerido";
      }
      if (exterior.length > 10) {
        return "El número exterior no puede tener más de 10 caracteres";
      }
      return undefined;
    }
    case "interior_number": {
      const interior = values.interior_number?.trim() ?? "";
      if (interior !== "" && interior.length > 10) {
        return "El número interior no puede tener más de 10 caracteres";
      }
      return undefined;
    }
    case "zip_code": {
      const zip = values.zip_code?.trim() ?? "";
      if (zip === "") {
        return "El código postal es requerido";
      }
      if (!/^\d{5}$/.test(zip)) {
        return "Debe tener 5 dígitos";
      }
      return undefined;
    }
    case "colonia": {
      const colonia = values.colonia?.trim() ?? "";
      if (colonia === "") {
        return "La colonia es requerida";
      }
      if (colonia.length > 80) {
        return "La colonia no puede tener más de 80 caracteres";
      }
      return undefined;
    }
    case "city": {
      const city = values.city?.trim() ?? "";
      if (city === "") {
        return "La ciudad es requerida";
      }
      if (city.length > 80) {
        return "La ciudad no puede tener más de 80 caracteres";
      }
      return undefined;
    }
    case "state": {
      const state = values.state?.trim() ?? "";
      if (state === "") {
        return "El estado es requerido";
      }
      if (state.length > 120) {
        return "El estado no puede tener más de 120 caracteres";
      }
      return undefined;
    }
    default:
      return undefined;
  }
};

export const arePropertyFieldsValid = (
  values: PropertyFieldValues,
  options: PropertyValidationOptions = {},
): boolean => {
  return PROPERTY_FIELD_KEYS.every(
    (key) => getPropertyFieldError(key, values, options) === undefined,
  );
};
